/** @jsxImportSource @emotion/react */

import styled from "@emotion/styled";
import Grid from "@mui/material/Grid";
import { useAtom } from "jotai";
import { useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { size } from "../../R";
import { useModal } from "../hooks/useDialog";
import { useHeaderCloseButton } from "../hooks/useHeaderCloseButton";
import type { HeaderProps } from "../interfaces/HeaderProps";
import { HeaderStore } from "../stores/header";
import { CloseButtonRightAlign } from "./CloseButton";
import { HeaderBackButton } from "./Header/HeaderBackButton";
import { HeaderBonusxLogo } from "./Header/HeaderBonusxLogo";
import HeaderContainer from "./Header/HeaderContainer";
import HeaderGuidedMode from "./Header/HeaderGuidedMode";
import { VisibleActions } from "./Header/VisibleActions";
import { Title } from "./common/Title";

const GridAlignRight = styled(Grid)`
  display: flex;
  justify-content: flex-end;
`;

function Header(props: HeaderProps): JSX.Element {
  const navigate = useNavigate();
  const [header, setHeader] = useAtom(HeaderStore);

  const { open: openModal } = useModal({
    children: props?.closeButton?.modal,
  });

  const closeButton = useHeaderCloseButton();
  const visibleActions = header.visibleActions || [];

  useEffect(() => {
    setHeader({ ...header, ...props.options });
  }, [props.options]);

  return (
    <HeaderContainer>
      <Grid container alignItems="center">
        <Grid item xs={3}>
          {header.backButton ? (
            <HeaderBackButton />
          ) : (
            <HeaderBonusxLogo {...{ closeButton, showLogo: props.showLogo }}>
              {props.guidedMode?.visible && <HeaderGuidedMode />}
            </HeaderBonusxLogo>
          )}
        </Grid>

        <Grid item xs={6} css={{ textAlign: "center" }}>
          {header.title && <Title variant="h3">{header.title}</Title>}
        </Grid>

        <GridAlignRight item xs={3}>
          {closeButton && (
            <Grid item xs>
              <CloseButtonRightAlign
                css={{ marginRight: size.padding.small }}
                onClick={() => {
                  if (props?.closeButton?.modal) {
                    return openModal();
                  }
                  if (header?.closeButton?.url) {
                    return navigate(header?.closeButton?.url);
                  }
                  return navigate(-1);
                }}
              />
            </Grid>
          )}
          {visibleActions.length > 0 && <VisibleActions />}
          {header?.customActionComponent}
        </GridAlignRight>
      </Grid>
    </HeaderContainer>
  );
}

export default Header;
